/**
 * useSessions - Hook for fetching and managing chat sessions
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { listSessions, deleteSession, closeSession, getSession } from "@/utils/maaran";
import { getMaaranUserContext, hasMaaranToken } from "@/utils/easygtm";
import toast from "react-hot-toast";

/**
 * Hook to fetch all sessions
 * Only fetches when user has a valid MAARAN token
 */
export const useSessions = ({ page = 1, pageSize = 20, isActive } = {}) => {
  const hasToken = hasMaaranToken();
  const selectedBrand = useSelector((state) => state?.common?.brand);
  const { brandId: fallbackBrandId } = getMaaranUserContext();
  const brandId = selectedBrand?.value || fallbackBrandId;

  return useQuery({
    queryKey: ["maaran-sessions", page, pageSize, isActive, brandId],
    queryFn: () => listSessions({ page, pageSize, isActive, brandId }),
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
    enabled: hasToken, // Only fetch if token exists
  });
};

/**
 * Hook to fetch a single session with messages
 */
export const useSessionDetail = (sessionId) => {
  return useQuery({
    queryKey: ["maaran-session", sessionId],
    queryFn: () => getSession(sessionId),
    enabled: !!sessionId,
    staleTime: 10000, // 10 seconds
  });
};

/**
 * Hook to delete a session
 */
export const useDeleteSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maaran-sessions"] });
      toast.success('Session Deleted Successfully!');
    },
  });
};

/**
 * Hook to close/archive a session
 */
export const useCloseSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: closeSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maaran-sessions"] });
    },
  });
};

export default useSessions;